import type { GrpcClient } from '@pushwoosh/http-client';

import { createTagsGrpcBridge, type TagsGrpcBridge } from './tags/tags';
import { createEventsGrpcBridge, type EventsGrpcBridge } from './events/events';
import {
  createSegmentsGrpcBridge,
  type SegmentsGrpcBridge,
} from './segments/segments';
import {
  createEmailTemplatesGrpcBridge,
  type EmailTemplatesGrpcBridge,
} from './emailTemplates/emailTemplates';
import { createGpt3GrpcBridge, type Gpt3GrpcBridge } from './gpt3';

type GrpcClientPack = {
  grpcClient: GrpcClient;
  billingGrpcClient: GrpcClient;
  journeyGrpcClient: GrpcClient;
  gpt3GrpcClient: GrpcClient;
};

export type GrpcBridgeMethods = {
  tags: TagsGrpcBridge;
  events: EventsGrpcBridge;
  segments: SegmentsGrpcBridge;
  emailTemplates: EmailTemplatesGrpcBridge;
  gpt3: Gpt3GrpcBridge;
};

export type GrpcBridge = GrpcBridgeMethods & {
  clients: GrpcClientPack;
  setEnableLogging: (enable: boolean) => void;
};

function applyLoggingProxy(
  bridgeMethods: GrpcBridgeMethods,
  getEnableLogging: () => boolean,
): GrpcBridgeMethods {
  let count = 0;
  return Object.keys(bridgeMethods).reduce((acc, namespace) => {
    acc[namespace] = Object.keys(
      bridgeMethods[namespace as keyof GrpcBridgeMethods],
    ).reduce((acc2, method) => {
      acc2[method] = async (args: any) => {
        const startTime = Date.now();
        const id = count++;
        if (getEnableLogging()) {
          console.log(`grpc #${id} call: ${namespace}.${method}`, args);
        }
        try {
          const result = await (bridgeMethods as any)[namespace][method](args);
          if (getEnableLogging()) {
            console.log(
              `grpc #${id} result: ${namespace}.${method} ${
                Date.now() - startTime
              }ms`,
              result,
            );
          }
          return result;
        } catch (error) {
          console.log(
            `grpc #${id} error: ${namespace}.${method} ${
              Date.now() - startTime
            }ms`,
            error,
          );
          throw error;
        }
      };
      return acc2;
    }, {} as any);
    return acc;
  }, {} as any);
}

export function createGrpcBridge(params: GrpcClientPack): GrpcBridge {
  let enableLogging = false;
  const getEnableLogging = () => enableLogging;
  const proxied = applyLoggingProxy(
    {
      tags: createTagsGrpcBridge(params.grpcClient),
      events: createEventsGrpcBridge(params.grpcClient),
      segments: createSegmentsGrpcBridge(params.grpcClient),
      emailTemplates: createEmailTemplatesGrpcBridge(params.grpcClient),
      gpt3: createGpt3GrpcBridge(params.gpt3GrpcClient),
    },
    getEnableLogging,
  );
  return {
    clients: params,
    setEnableLogging: (enable: boolean) => {
      enableLogging = enable;
    },
    ...proxied,
  };
}
